import { Button, Icon, Tooltip, token } from "@may-i/mash-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { StepCrop, preloadOpenCv } from "./multi-camera/StepCrop";
import { StepSelect } from "./multi-camera/StepSelect";
import { StepSplit, type CroppedImage } from "./multi-camera/StepSplit";
import { StepUpload } from "./multi-camera/StepUpload";

type Step = 1 | 2 | 3 | 4;

interface MultiCameraModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (blobs: Blob[]) => void;
}

export function MultiCameraModal({ open, onClose, onComplete }: MultiCameraModalProps) {
  const { t } = useTranslation("resource");
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // Tooltip 포털 컨테이너 — <dialog showModal> 의 top layer 안에 렌더해야 backdrop 위로 보임
  const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [croppedImages, setCroppedImages] = useState<CroppedImage[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [cropResetSignal, setCropResetSignal] = useState(0);
  const [splitResetSignal, setSplitResetSignal] = useState(0);
  const splitRef = useRef<(() => Promise<void>) | null>(null);

  // 원근보정 엔진(OpenCV) 준비 여부 — 2단계 "다음" 게이팅용
  const [cvReady, setCvReady] = useState(false);
  // F2 batch 경로(crop/split 스킵하고 step4 직행)인지 — 이전 버튼이 빈 step3/2 로 가지 않게 (RV3)
  const [batchMode, setBatchMode] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    }
  }, [open]);

  // 모달 열 때 원근보정 엔진(약 9MB)을 미리 로드 시작 — 2단계 도달 시 대기 최소화
  useEffect(() => {
    if (open) preloadOpenCv();
  }, [open]);

  const resetState = useCallback(() => {
    setStep(1);
    setOriginalImageUrl(null);
    setCroppedImageUrl(null);
    setCroppedImages([]);
    setSelectedIds([]);
    setCropResetSignal(0);
    setSplitResetSignal(0);
    setBatchMode(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleImageChange = useCallback((url: string) => {
    setOriginalImageUrl(url);
    setBatchMode(false); // 단일 파일 경로 — crop/split 정상 흐름
  }, []);

  // F2: 여러 이미지가 업로드되면 crop/split 건너뛰고 바로 선택 단계로
  const handleBatchFiles = useCallback((files: File[]) => {
    const images: CroppedImage[] = files.map((f, i) => ({
      id: `batch-${i}-${Date.now()}`,
      blob: f,
    }));
    setCroppedImages(images);
    setSelectedIds(images.map((img) => img.id));
    setBatchMode(true);
    setStep(4);
  }, []);

  const handleCropComplete = useCallback((url: string) => {
    setCroppedImageUrl(url);
  }, []);

  const handleSplitComplete = useCallback((images: CroppedImage[]) => {
    setCroppedImages(images);
    setSelectedIds(images.map((i) => i.id));
    setStep(4);
  }, []);

  const handleNext = useCallback(async () => {
    if (step === 1 && originalImageUrl) {
      setStep(2);
    } else if (step === 2) {
      // Apply crop (synchronous bounding-box crop)
      const applyFn = (window as unknown as Record<string, () => void>)
        .__multiCameraCropApply;
      if (applyFn) {
        applyFn();
      }
      setStep(3);
    } else if (step === 3) {
      // Trigger split
      await splitRef.current?.();
    } else if (step === 4) {
      // Complete
      const selectedBlobs = croppedImages
        .filter((img) => selectedIds.includes(img.id))
        .map((img) => img.blob);
      onComplete(selectedBlobs);
      handleClose();
    }
  }, [step, originalImageUrl, croppedImages, selectedIds, onComplete, handleClose]);

  // Enter 단축키로 다음 단계 진행
  const isNextDisabledNow =
    (step === 1 && !originalImageUrl) ||
    (step === 2 && !cvReady) ||
    (step === 4 && selectedIds.length === 0);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      // 입력 중인 곳에서는 무시
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (isNextDisabledNow) return;
      e.preventDefault();
      void handleNext();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, isNextDisabledNow, handleNext]);

  const handleStepClick = useCallback(
    (targetStep: Step) => {
      if (targetStep < step) {
        setStep(targetStep);
      }
    },
    [step],
  );

  const handleReplaceImage = useCallback(() => {
    // Trigger file input by resetting to step 1
    setStep(1);
    setOriginalImageUrl(null);
    setCroppedImageUrl(null);
    setCroppedImages([]);
    setSelectedIds([]);
  }, []);

  const handleResetCrop = useCallback(() => {
    setCropResetSignal((s) => s + 1);
  }, []);

  const handleResetSplit = useCallback(() => {
    setSplitResetSignal((s) => s + 1);
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(croppedImages.map((i) => i.id));
  }, [croppedImages]);

  const getStepHint = () => {
    switch (step) {
      case 1:
        return t("multiCamera.uploadHint");
      case 2:
        return t("multiCamera.cropHint");
      case 3:
        return t("multiCamera.splitHint");
      case 4:
        return t("multiCamera.selectHint");
    }
  };

  const getActionButton = () => {
    switch (step) {
      case 1:
        return originalImageUrl ? (
          <Button variant="text" size="xSmall" onClick={handleReplaceImage}>
            {t("multiCamera.replaceImage")}
          </Button>
        ) : null;
      case 2:
        return (
          <Button variant="text" size="xSmall" onClick={handleResetCrop}>
            {t("multiCamera.resetCrop")}
          </Button>
        );
      case 3:
        return (
          <Button variant="text" size="xSmall" onClick={handleResetSplit}>
            {t("multiCamera.resetSplit")}
          </Button>
        );
      case 4: {
        const allSelected = selectedIds.length === croppedImages.length;
        return (
          <Button
            variant="text"
            size="xSmall"
            onClick={allSelected ? handleDeselectAll : handleSelectAll}
          >
            {allSelected ? t("multiCamera.deselectAll") : t("multiCamera.selectAll")}
          </Button>
        );
      }
    }
  };

  const isNextDisabled = () => {
    if (step === 1) return !originalImageUrl;
    if (step === 2) return !cvReady;
    if (step === 4) return selectedIds.length === 0;
    return false;
  };

  const getNextLabel = () => {
    if (step === 4) {
      return t("multiCamera.addCameras", { count: selectedIds.length });
    }
    return t("multiCamera.next");
  };

  const getNextTooltip = () => {
    if (step === 2 && !cvReady) {
      return "잘라낸 이미지를 반듯하게 펴는 기능을 불러오는 중이에요. 잠시 후 진행할 수 있어요.";
    }
    return `${getNextLabel()} (Enter)`;
  };

  if (!open) return null;

  const stepLabels: { step: Step; key: string }[] = [
    { step: 1, key: "multiCamera.step1" },
    { step: 2, key: "multiCamera.step2" },
    { step: 3, key: "multiCamera.step3" },
    { step: 4, key: "multiCamera.step4" },
  ];

  return (
    <StyledDialog
      ref={(el) => {
        dialogRef.current = el;
        setDialogEl(el);
      }}
      onCancel={(e) => {
        // ESC 또는 파일 피커 취소 등으로 fire되는 cancel 이벤트로는 모달이 닫히지 않도록 차단.
        // 모달 close는 X 버튼 또는 step 4 완료 시에만 트리거됨.
        e.preventDefault();
      }}
    >
      <Header>
        <HeaderTitle>{t("multiCamera.title")}</HeaderTitle>
        <CloseButton onClick={handleClose}>
          <Icon icon="ic_close" width={20} />
        </CloseButton>
      </Header>

      <StepIndicatorBar>
        {stepLabels.map((s, i) => (
          <StepItem key={s.step}>
            {i > 0 && <ChevronIcon icon="ic_chevron_right" width={16} />}
            <StepButton
              variant="text"
              size="xSmall"
              $active={step === s.step}
              disabled={s.step > step}
              onClick={() => handleStepClick(s.step)}
            >
              {t(s.key)}
            </StepButton>
          </StepItem>
        ))}
      </StepIndicatorBar>

      <SubHeader>
        <HintText>
          {step === 3
            ? `${getStepHint()} (${t("multiCamera.splitShortcut")})`
            : getStepHint()}
        </HintText>
        {getActionButton()}
      </SubHeader>

      <ContentArea>
        {step === 1 && (
          <StepUpload imageUrl={originalImageUrl} onImageChange={handleImageChange} onBatchFiles={handleBatchFiles} />
        )}
        {step === 2 && originalImageUrl && (
          <StepCrop
            imageUrl={originalImageUrl}
            onCropComplete={handleCropComplete}
            resetSignal={cropResetSignal}
            onCvReady={setCvReady}
          />
        )}
        {step === 3 && (croppedImageUrl || originalImageUrl) && (
          <StepSplit
            imageUrl={croppedImageUrl || originalImageUrl!}
            resetSignal={splitResetSignal}
            onSplitComplete={handleSplitComplete}
            splitRef={splitRef}
          />
        )}
        {step === 4 && (
          <StepSelect
            images={croppedImages}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        )}
      </ContentArea>

      <Footer>
        {step > 1 && (
          <Tooltip
            trigger={
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={() => setStep((s) => (batchMode && s === 4 ? 1 : s - 1) as Step)}
              >
                {t("multiCamera.previous")}
              </Button>
            }
            side="top"
            sideOffset={8}
            container={dialogEl}
          >
            {t("multiCamera.previousTooltip")}
          </Tooltip>
        )}
        <Tooltip
          trigger={
            <span>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isNextDisabled()}
                onClick={handleNext}
              >
                {getNextLabel()}
              </Button>
            </span>
          }
          side="top"
          sideOffset={8}
          container={dialogEl}
        >
          {getNextTooltip()}
        </Tooltip>
      </Footer>
    </StyledDialog>
  );
}

const StyledDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: min(1100px, 90vw);
  height: min(800px, 90vh);
  margin: auto;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);
  display: flex;
  flex-direction: column;

  &::backdrop {
    background: rgba(17, 17, 31, 0.32);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${token("colors.text.secondary")};
  border-radius: 4px;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const StepIndicatorBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px 0;
  flex-shrink: 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChevronIcon = styled(Icon)`
  color: ${token("colors.text.disabled")};
`;

const StepButton = styled(Button)<{ $active: boolean }>`
  && {
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    color: ${({ $active }) =>
      $active ? token("colors.text.primary") : token("colors.text.disabled")};
  }
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px 0;
  flex-shrink: 0;
`;

const HintText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  color: ${token("colors.text.secondary")};
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 12px 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 0 24px 20px;
  flex-shrink: 0;
`;
